import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FiGitBranch, FiMonitor, FiRadio, FiServer, FiShare2 } from "react-icons/fi";
import { getUbiquitiGatewaySummary } from "../../scripts/api_ubiquiti";

const GATEWAY_SUMMARY_QUERY_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 30000,
};

const getWrappedPayload = (response) => {
  const firstPayload = response?.data || response || {};

  return firstPayload?.data || firstPayload;
};

const getDisplayValue = (value, fallback = "-") => (
  value === undefined || value === null || value === "" ? fallback : value
);

const LoadingValue = ({ children, className = "", isLoading, width = "42px" }) => {
  if (isLoading) {
    return <span className={`ubiquiti-loading-value ${className}`.trim()} style={{ "--loading-width": width }} aria-label="Loading"></span>;
  }

  return <span className={className}>{getDisplayValue(children)}</span>;
};

const StatBadge = ({ icon, isLoading, label, value }) => (
  <div
    className="ubiquiti-stat-badge"
    aria-label={`${label}: ${getDisplayValue(value)}`}
    data-tooltip={`${label}: ${getDisplayValue(value)}`}
    title={`${label}: ${getDisplayValue(value)}`}
  >
    <span className="ubiquiti-stat-icon">{icon}</span>
    <LoadingValue className="ubiquiti-stat-value" isLoading={isLoading} width="18px">{value}</LoadingValue>
  </div>
);

const UbiquitiGatewaySummary = ({ apiUrl, enabled = true, fallbackName = "-", onQueryStateChange, refreshKey = 0, siteId }) => {
  const { t } = useTranslation();
  const gatewaySummaryQuery = useQuery({
    ...GATEWAY_SUMMARY_QUERY_OPTIONS,
    queryKey: ["ubiquiti-gateway-summary", apiUrl, siteId],
    queryFn: () => getUbiquitiGatewaySummary(apiUrl, siteId),
    enabled: enabled && !!apiUrl && !!siteId,
  });
  const { refetch } = gatewaySummaryQuery;
  const summary = useMemo(() => getWrappedPayload(gatewaySummaryQuery.data), [gatewaySummaryQuery.data]);
  const isInitialLoading = !gatewaySummaryQuery.data && (!enabled || gatewaySummaryQuery.isLoading);
  const name = getDisplayValue(summary.name, fallbackName);
  const statusDisplay = getDisplayValue(summary.status?.display);
  const gatewayIp = getDisplayValue(summary.gatewayIp?.display);
  const systemUptime = getDisplayValue(summary.systemUptime?.display);
  const networkVersion = getDisplayValue(summary.network?.version);
  const networkStatus = getDisplayValue(summary.network?.update?.display);
  const networkRollback = summary.network?.rollback || null;
  const unifiOsVersion = getDisplayValue(summary.unifiOs?.version);
  const unifiOsStatus = getDisplayValue(summary.unifiOs?.update?.display);
  const devicesDisplay = getDisplayValue(summary.devices?.display);

  useEffect(() => {
    if (enabled && refreshKey && siteId) {
      refetch();
    }
  }, [enabled, refreshKey, refetch, siteId]);

  useEffect(() => {
    if (!onQueryStateChange) {
      return;
    }

    onQueryStateChange({
      siteId,
      isError: gatewaySummaryQuery.isError,
      isFetched: gatewaySummaryQuery.isFetched,
      isFetching: gatewaySummaryQuery.isFetching,
    });
  }, [
    gatewaySummaryQuery.isError,
    gatewaySummaryQuery.isFetched,
    gatewaySummaryQuery.isFetching,
    onQueryStateChange,
    siteId,
  ]);

  useEffect(() => {
    window.__ubiquitiGatewaySummaryDebug = {
      apiCall: `/ubiquiti/sites/${siteId}/gateway-summary`,
      enabled,
      queryStatus: {
        isLoading: gatewaySummaryQuery.isLoading,
        isFetching: gatewaySummaryQuery.isFetching,
        isError: gatewaySummaryQuery.isError,
        error: gatewaySummaryQuery.error,
      },
      rawResponse: gatewaySummaryQuery.data,
      displayedValues: {
        name,
        status: statusDisplay,
        counts: summary.counts || null,
        gatewayIp,
        systemUptime,
        networkVersion,
        networkStatus,
        networkRollback,
        unifiOsVersion,
        unifiOsStatus,
        devicesDisplay,
      },
    };
  }, [
    devicesDisplay,
    enabled,
    gatewayIp,
    gatewaySummaryQuery.data,
    gatewaySummaryQuery.error,
    gatewaySummaryQuery.isError,
    gatewaySummaryQuery.isFetching,
    gatewaySummaryQuery.isLoading,
    name,
    networkRollback,
    networkStatus,
    networkVersion,
    siteId,
    statusDisplay,
    summary.counts,
    systemUptime,
    unifiOsStatus,
    unifiOsVersion,
  ]);

  return (
    <article className="ubiquiti-gateway-card">
      <div className="ubiquiti-gateway-title">
        <h2>
          <LoadingValue isLoading={isInitialLoading} width="120px">{name}</LoadingValue>
        </h2>
        <span className={summary.status?.online === false ? "ubiquiti-status-dot is-offline" : "ubiquiti-status-dot"}>
          <LoadingValue isLoading={isInitialLoading} width="48px">{statusDisplay}</LoadingValue>
        </span>
      </div>

      <div className="ubiquiti-mini-stats">
        <StatBadge icon={<FiServer />} isLoading={isInitialLoading} label={t("Gateways")} value={summary.counts?.gateways} />
        <div className="ubiquiti-stat-separator" aria-hidden="true"><span></span></div>
        <StatBadge icon={<FiGitBranch />} isLoading={isInitialLoading} label={t("Wired")} value={summary.counts?.wired} />
        <div className="ubiquiti-stat-separator" aria-hidden="true"><span></span></div>
        <StatBadge icon={<FiRadio />} isLoading={isInitialLoading} label={t("Access points")} value={summary.counts?.accessPoints} />
        <div className="ubiquiti-stat-separator" aria-hidden="true"><span></span></div>
        <StatBadge icon={<FiShare2 />} isLoading={isInitialLoading} label={t("Non-network")} value={summary.counts?.nonNetwork} />
        <div className="ubiquiti-stat-separator" aria-hidden="true"><span></span></div>
        <StatBadge icon={<FiMonitor />} isLoading={isInitialLoading} label={t("Clients")} value={summary.counts?.clients} />
      </div>

      <div className="ubiquiti-info-list">
        <div>
          <span>{t("Gateway IP")}</span>
            <LoadingValue isLoading={isInitialLoading} width="78px">{gatewayIp}</LoadingValue>
        </div>
        <div>
          <span>{t("System uptime")}</span>
            <LoadingValue isLoading={isInitialLoading} width="62px">{systemUptime}</LoadingValue>
        </div>
        <div>
          <span>{t("Network")} {networkVersion}</span>
          <div className={summary.network?.update?.available ? "is-linkish" : ""}>
            <LoadingValue isLoading={isInitialLoading} width="58px">{networkStatus}</LoadingValue>
          </div>
        </div>
        <div>
          <span>UniFi OS {unifiOsVersion}</span>
          <div className={summary.unifiOs?.update?.available ? "is-linkish" : ""}>
            <LoadingValue isLoading={isInitialLoading} width="58px">{unifiOsStatus}</LoadingValue>
          </div>
        </div>
        <div>
          <span>{t("Equipment")}</span>
          <div className={String(summary.devices?.status || "").toLowerCase() === "updateavailable" ? "is-linkish" : ""}>
            <LoadingValue isLoading={isInitialLoading} width="58px">{devicesDisplay}</LoadingValue>
          </div>
        </div>
      </div>
    </article>
  );
};

export default UbiquitiGatewaySummary;
